import { useState } from 'react'
import { AlertTriangle, Check, Loader2, Mountain, Trash2, Upload, X } from 'lucide-react'

import { useRoadbook } from '../../data/roadbookContext'
import { dauerAbweichung, etappenZeit, MIN_ABDECKUNG, prozent } from '../../lib/fahrzeit'
import { anzahl, clock, duration, isoDate, num } from '../../lib/format'
import {
  mergeTrackParts,
  STOP_MIN_SECONDS,
  STOP_RADIUS_M,
  trackFromGpx,
  trackParts,
  type TrackKonflikt,
} from '../../lib/gpx'
import type { DiaryEntry, DiaryTrack, Stage, TrackElevation, TrackPart } from '../../lib/types'
import {
  angenommene,
  ersteKandidaten,
  leseDateien,
  neuBeurteilt,
  warnungenAus,
  type Kandidat,
  type Lesefehler,
} from './mehrdateien'

/**
 * Der aufgezeichnete Weg einer Etappe: GPX-Datei einlesen, zeigen, was
 * darinsteht, und daraus uebernehmen, was ins Tagebuch gehört.
 *
 * Dieselbe Machart wie die Streckenberechnung bei den Etappen
 * (features/stages/StageRouteEditor.tsx): die Datei wird auf Knopfdruck
 * gelesen und der Track bleibt danach im Tagebuch liegen - fertig gerechnet,
 * ausgedünnt und offline verfügbar. Übernommen in die Felder wird nichts von
 * selbst. Die gefahrenen Kilometer aus der Aufzeichnung sind näher an der
 * Wahrheit als die Planung, aber der Tachostand ist noch näher dran, und ein
 * Wert, der sich unbemerkt in das Tagebuch schreibt, wäre eine Behauptung.
 * Also stehen die Zahlen da, daneben, was gerade eingetragen ist, und der
 * Nutzer drückt.
 *
 * Eine zweite Datei ersetzt die erste nicht, sondern kommt dazu. Das Gerät
 * geht nach fünf Minuten Stillstand in Bereitschaft: ein Mittagshalt schneidet
 * die Aufzeichnung in zwei Dateien, und wer am Abend die zweite einliest, will
 * den Vormittag behalten. Zusammengelegt wird nach der Uhr, und was schon
 * dagewesen ist, wird nicht noch einmal gezählt (trackKonflikt) - stattdessen
 * steht die Frage da, und der Nutzer drückt. Herausnehmen lässt sich jeder
 * Teil einzeln, auch morgen noch.
 *
 * Aus demselben Grund lassen sich mehrere Dateien auf einmal wählen: ein
 * gewöhnlicher Arbeitstag ergibt vier davon, weil das Gerät je Fahrt eine
 * schreibt. Sie einzeln nachzuladen hiesse, viermal dieselbe Maske zu bedienen
 * und dreimal eine Zwischensumme zu sehen, die den Tag nicht meint.
 *
 * Wo es dabei etwas zu entscheiden gibt, wird gefragt, und sonst nicht: eine
 * einzelne Datei ohne Einwand geht durch wie bisher. Sobald mehr als eine Datei
 * gewählt ist, steht erst die Liste da - mit der Reihenfolge, den Zeiträumen
 * und dem, was sich beisst - und der Nutzer drückt.
 */
export function TrackPanel({
  stage,
  entry,
  nurLesen = false,
}: {
  stage: Stage
  entry: DiaryEntry
  /** Ohne Schreibrecht bleiben nur die Zahlen stehen, siehe unten. */
  nurLesen?: boolean
}) {
  const { setDiary } = useRoadbook()
  const [laedt, setLaedt] = useState(false)
  /** Die Dateien, aus denen gar nichts geworden ist, mit ihrem Namen. */
  const [fehler, setFehler] = useState<Lesefehler[]>([])
  /** Was gerade gemeldet wurde, etwa dass ein zweiter Teil dazugekommen ist. */
  const [meldung, setMeldung] = useState<string | null>(null)
  /**
   * Die gelesenen Aufzeichnungen, solange sie noch nicht geschrieben sind.
   *
   * Sie warten hier, bis der Nutzer entschieden hat - geschrieben werden sie
   * nur auf Knopfdruck. Leer heisst: es steht nichts aus. Eine einzelne Datei
   * ohne Einwand kommt hier gar nicht erst an, die geht durch.
   */
  const [kandidaten, setKandidaten] = useState<Kandidat[]>([])
  /**
   * Was an den zuletzt gelesenen Dateien nicht in Ordnung war, obwohl ein Track
   * herausgekommen ist. Steht nur bis zum nächsten Import und wird nicht
   * gespeichert - sichtbar ist die Warnung damit genau in dem Moment, in dem
   * der Nutzer entscheidet, ob er die Zahlen übernimmt.
   */
  const [warnungen, setWarnungen] = useState<string[]>([])

  const track = entry.track
  const teile = track ? trackParts(track) : []

  /**
   * Legt gelesene Dateien zu dem, was schon da ist.
   *
   * Auch der erste Import läuft hier durch: eine leere Teileliste ergibt genau
   * den Track der einen Datei, und es gibt nur einen Weg ins Tagebuch.
   * Sortiert, verschmolzen und gegen die Lücken dazwischen gerechnet wird in
   * mergeTrackParts - hier wird nichts nachgerechnet.
   */
  function uebernehmen(neue: TrackPart[]) {
    if (neue.length === 0) return
    const zusammen = mergeTrackParts([...teile, ...neue])
    if (!zusammen) return
    setDiary(stage.id, { track: zusammen })
    setKandidaten([])
    setMeldung(
      teile.length === 0 && neue.length === 1
        ? null
        : `${neue.length === 1 ? neue[0]!.fileName || 'Die Datei' : anzahl(neue.length, 'Datei ist', 'Dateien sind')} dazugekommen. ` +
            `Der Track besteht jetzt aus ${String(zusammen.parts?.length ?? 1)} Teilen.`,
    )
  }

  /** Nimmt einen Teil wieder heraus und rechnet den Rest neu zusammen. */
  function entferneTeil(index: number) {
    const rest = teile.filter((_, i) => i !== index)
    const weg = teile[index]
    setDiary(stage.id, { track: mergeTrackParts(rest) ?? undefined })
    setKandidaten([])
    setMeldung(
      rest.length === 0
        ? null
        : `${weg?.fileName || 'Der Teil'} ist herausgenommen. Die Zahlen oben sind ohne ihn gerechnet.`,
    )
  }

  async function nimmDateien(liste: FileList | null) {
    if (!liste || liste.length === 0) return
    setLaedt(true)
    setFehler([])
    setWarnungen([])
    setMeldung(null)
    setKandidaten([])

    // Ein Zeitpunkt für die ganze Auswahl: "gelesen am" meint diesen
    // Knopfdruck, und bei Dateien ohne Uhr entscheidet er ausserdem über die
    // Reihenfolge - die soll die der Auswahl bleiben.
    const jetzt = Date.now()
    const { gelesen, fehler: gescheitert } = await leseDateien(liste, (xml, name) =>
      trackFromGpx(xml, name, jetzt),
    )
    setFehler(gescheitert)
    setWarnungen(warnungenAus(gelesen))

    // Erst nachsehen, ob dieselbe Fahrt schon dasteht - im Tagebuch oder in
    // der Auswahl selbst. Ungefragt dazulegen wären doppelte Kilometer, und
    // die sehen genauso aus wie richtige.
    const gelesene = ersteKandidaten(
      teile,
      gelesen.map((g) => trackParts(g.wert.track)[0]!),
    )

    // Eine einzelne Datei ohne Einwand geht durch wie bisher: es gibt nichts
    // auszuwählen und nichts zu ordnen, und ein zusätzlicher Knopfdruck vor
    // dem Normalfall wäre nur ein Knopfdruck mehr. Sobald mehrere Dateien im
    // Spiel sind, steht erst die Liste da.
    if (liste.length === 1 && gelesene.length === 1 && gelesene[0]!.konflikt === null) {
      uebernehmen([gelesene[0]!.teil])
    } else {
      setKandidaten(gelesene)
    }
    setLaedt(false)
  }

  /** Setzt einen Haken und rechnet die Beurteilungen der ganzen Liste neu. */
  function setzeHaken(index: number, aktiv: boolean) {
    setKandidaten((alt) =>
      neuBeurteilt(
        teile,
        alt.map((k, i) => (i === index ? { ...k, aktiv } : k)),
      ),
    )
  }

  // Nur lesende Rolle: die Zahlen der Aufzeichnung bleiben sichtbar, alles
  // Übrige an diesem Block ist Einlesen, Übernehmen und Entfernen - lauter
  // Handgriffe, die diese Rolle nicht hat. Ein leerer Block mit dem Hinweis,
  // dass noch keine Datei da ist, wäre für sie nur eine Aufforderung ins
  // Leere.
  if (nurLesen) {
    if (!track) return null
    return (
      <section className="track-block">
        <h5 className="block-titel">
          <Mountain size={13} /> Aufzeichnung
        </h5>
        <TrackWerte stage={stage} track={track} entry={entry} />
      </section>
    )
  }

  const kmVorschlag = track ? String(Math.round(track.distanceKm)) : ''
  // Ohne Zeitstempel in der Datei gibt es kein Datum vorzuschlagen. Das kommt
  // vor: manche Programme schreiben nur Koordinaten.
  const datumVorschlag = track && track.startedAt !== null ? isoDate(track.startedAt) : ''

  return (
    <section className="track-block">
      <h5 className="block-titel">
        <Mountain size={13} /> Aufzeichnung
      </h5>

      {!track && (
        <p className="leer kein-druck">
          Noch keine GPX-Datei. Aus einer Aufzeichnung ergeben sich die tatsächlich gefahrenen
          Kilometer, Fahrzeit und Standzeit, die Pausen und das Höhenprofil.
        </p>
      )}

      {track && <TrackWerte stage={stage} track={track} entry={entry} />}

      {/*
        Die Teile stehen unter den Zahlen, die aus ihnen entstanden sind, und
        jeder trägt seinen eigenen Knopf. Das ist der Weg zurück: wer die
        Datei des Vortags auf die heutige Etappe gelegt hat, nimmt genau sie
        wieder heraus - ohne die richtige noch einmal zu suchen und ohne den
        Rest des Tages zu verlieren. Bei nur einem Teil sagt die Zeile nichts,
        was nicht ohnehin unten steht; dann tut es "Track entfernen".
      */}
      {teile.length > 1 && (
        <ol className="track-stopps kein-druck">
          {teile.map((teil, i) => (
            <li key={`${teil.fingerprint}-${String(teil.importedAt)}-${String(i)}`}>
              <b>{num(teil.distanceKm)} km</b>
              <span>{teilZeitraum(teil)}</span>
              <span className="track-ort">{teil.fileName || 'GPX-Datei'}</span>
              <button
                type="button"
                className="knopf still"
                onClick={() => {
                  entferneTeil(i)
                }}
              >
                <X size={13} /> Herausnehmen
              </button>
            </li>
          ))}
        </ol>
      )}

      {/*
        Über den Knöpfen und nicht unter ihnen: die Warnung gehört zu den
        Zahlen darüber, und wer sie erst unter dem Knopf läse, hätte schon
        gedrückt.
      */}
      {/*
        Der Schlüssel ist die Stelle in der Liste und nicht der Satz selbst:
        wer dieselbe abgeschnittene Datei zweimal wählt, bekommt zweimal
        denselben Satz, und doppelte Schlüssel liessen den zweiten
        verschwinden. Die Liste hat keine Eingabefelder und wird nur als Ganzes
        ersetzt, nämlich beim nächsten Einlesen.
      */}
      {warnungen.map((warnung, i) => (
        <p className="karte-warnung kein-druck" key={i}>
          <AlertTriangle size={13} /> {warnung}
        </p>
      ))}

      {/*
        Die gelesenen Dateien stehen zwischen den Zahlen und den Knöpfen, und
        das ist die Stelle, an der eine gelesene Datei noch nicht ins Tagebuch
        gegangen ist. Jede nennt ihren Zeitraum, ihre Kilometer und - wo es
        etwas zu sagen gibt - was gegen sie spricht; jede lässt sich abwählen.
        Die Reihenfolge ist die der Uhr und nicht die der Auswahl.
      */}
      {kandidaten.length > 0 && (
        <div className="kein-druck">
          <ol className="track-stopps">
            {kandidaten.map((kandidat, i) => (
              <li key={`${kandidat.teil.fingerprint}-${String(i)}`}>
                <b>{num(kandidat.teil.distanceKm)} km</b>
                <span>{teilZeitraum(kandidat.teil)}</span>
                <span className="track-ort">{kandidat.teil.fileName || 'GPX-Datei'}</span>
                <label className="knopf still">
                  <input
                    type="checkbox"
                    checked={kandidat.aktiv}
                    onChange={(e) => {
                      setzeHaken(i, e.target.checked)
                    }}
                  />
                  Übernehmen
                </label>
              </li>
            ))}
          </ol>

          {kandidaten.map(
            (kandidat, i) =>
              kandidat.konflikt && (
                <p className="karte-warnung" key={`einwand-${String(i)}`}>
                  <AlertTriangle size={13} />{' '}
                  {konfliktSatz(kandidat.konflikt, kandidat.teil, kandidat.imBestand)}
                </p>
              ),
          )}

          <div className="knopfreihe">
            <button
              type="button"
              className={kandidaten.some((k) => k.aktiv && k.konflikt) ? 'knopf gefahr' : 'knopf'}
              disabled={!kandidaten.some((k) => k.aktiv)}
              onClick={() => {
                uebernehmen(angenommene(kandidaten))
              }}
            >
              <Check size={14} />
              {kandidaten.some((k) => k.aktiv)
                ? `${anzahl(kandidaten.filter((k) => k.aktiv).length, 'Aufzeichnung', 'Aufzeichnungen')} übernehmen`
                : 'Nichts ausgewählt'}
            </button>
            <button
              type="button"
              className="knopf still"
              onClick={() => {
                setKandidaten([])
              }}
            >
              <X size={14} /> Verwerfen
            </button>
          </div>
        </div>
      )}

      {meldung && <p className="hinweis kein-druck">{meldung}</p>}

      {track && (
        <div className="knopfreihe kein-druck">
          <button
            type="button"
            className="knopf"
            disabled={entry.realKm === kmVorschlag && entry.date === datumVorschlag}
            onClick={() =>
              setDiary(stage.id, {
                realKm: kmVorschlag,
                ...(datumVorschlag ? { date: datumVorschlag } : {}),
              })
            }
          >
            <Check size={14} /> {num(track.distanceKm)} km
            {datumVorschlag && ` und ${datumVorschlag}`} übernehmen
          </button>
          <button
            type="button"
            className="knopf still"
            disabled={entry.realKm === kmVorschlag}
            onClick={() => setDiary(stage.id, { realKm: kmVorschlag })}
          >
            Nur Kilometer
          </button>
          <button
            type="button"
            className="knopf gefahr"
            onClick={() => {
              setDiary(stage.id, { track: undefined })
              setMeldung(null)
              setKandidaten([])
            }}
          >
            <Trash2 size={14} /> {teile.length > 1 ? 'Alle Teile entfernen' : 'Track entfernen'}
          </button>
        </div>
      )}

      <div className="knopfreihe kein-druck">
        <label className="knopf still">
          {laedt ? <Loader2 size={14} className="dreht" /> : <Upload size={14} />}
          {/*
            "Weitere" und nicht "Andere": die nächsten Dateien ersetzen die
            erste nicht, sie kommen dazu. Der Knopf soll sagen, was passiert.
          */}
          {laedt
            ? 'Dateien werden gelesen …'
            : track
              ? 'Weitere GPX-Dateien'
              : 'GPX-Dateien wählen'}
          <input
            type="file"
            accept=".gpx,application/gpx+xml,application/xml,text/xml"
            multiple
            hidden
            onChange={(e) => {
              void nimmDateien(e.target.files)
              // Sonst lässt sich dieselbe Datei nach einer Korrektur nicht
              // noch einmal wählen: der Wert des Feldes wäre unverändert.
              e.target.value = ''
            }}
          />
        </label>
      </div>

      {/*
        Je Datei eine Zeile und keine Sammelmeldung: wer vier Dateien gewählt
        hat, muss wissen, welche der vier nichts geworden ist - und dass die
        anderen drei oben in der Liste stehen.
      */}
      {fehler.map((f, i) => (
        <p className="karte-warnung kein-druck" key={i}>
          {f.name}: {f.grund}
        </p>
      ))}
    </section>
  )
}

/**
 * Der Zeitraum eines Teils, so kurz wie möglich.
 *
 * Das Datum steht dabei, auch wenn alle Teile vom selben Tag sind: genau daran
 * sieht man, dass die Datei des Vortags auf der falschen Etappe gelandet ist.
 */
function teilZeitraum(teil: TrackPart): string {
  if (teil.startedAt === null || teil.endedAt === null) return 'ohne Zeitstempel'

  const tag = isoDate(teil.startedAt)
  const bis = isoDate(teil.endedAt)
  const ende = `${clock(teil.endedAt)}${bis === tag ? '' : ` (${bis})`}`
  return `${tag} ${clock(teil.startedAt)}–${ende}`
}

/**
 * Was gegen das Hinzufügen spricht, in einem Satz.
 *
 * Der Satz nennt beides: woran es liegt und was es kostet. "Diese Datei war
 * schon da" allein wäre eine Feststellung; die doppelten Kilometer sind der
 * Grund, warum hier überhaupt jemand gefragt wird.
 *
 * Und er nennt, wo das Gegenstück steht. Seit mehrere Dateien auf einmal
 * gewählt werden können, liegt es nicht mehr zwangsläufig auf der Etappe: beim
 * Garmin enthält Current.gpx dieselbe Spur noch einmal, und wer sie neben der
 * Einzeldatei mitwählt, findet das Gegenstück drei Zeilen weiter oben. Ein
 * Satz, der ihn im Tagebuch suchen schickte, wäre dort falsch.
 *
 * @param imTagebuch Ob das Gegenstück auf der Etappe liegt. Sonst steht es in
 *   derselben Auswahl.
 */
function konfliktSatz(konflikt: TrackKonflikt, neu: TrackPart, imTagebuch: boolean): string {
  const wer = neu.fileName || 'Eine der Dateien'
  const woher = `${konflikt.teil.fileName || 'eine GPX-Datei'} (${teilZeitraum(konflikt.teil)})`
  const wo = imTagebuch ? 'liegt schon auf dieser Etappe' : 'steht schon in dieser Auswahl'

  if (konflikt.art === 'gleich') {
    return (
      `${wer}: genau diese Aufzeichnung ${wo} — ${woher}, gelesen am ` +
      `${isoDate(konflikt.teil.importedAt)}. Angehakt zählen ihre ` +
      `${num(neu.distanceKm)} km ein zweites Mal.`
    )
  }

  return (
    `${wer}: der Zeitraum überschneidet sich um ${duration(konflikt.sekunden)} mit ${woher}, ` +
    `und die ${wo}. Dieselbe Fahrt zweimal zu zählen macht Kilometer, Fahrzeit und Halte falsch.`
  )
}

/**
 * Die abgeleiteten Werte, das Profil und die Zeitleiste der Stopps.
 *
 * Die Uhr steht hier nicht mehr für sich allein. „Vier Stunden gefahren“ ist
 * eine Angabe über den Tag; „vier Stunden gefahren, dreieinhalb geplant“ ist
 * eine Auskunft darüber, ob der Plan trägt - und die ist der Grund, aus dem in
 * einer Aufzeichnung überhaupt Zeitstempel stehen. Gerechnet wird das in
 * lib/fahrzeit.ts, hier steht nur, was davon in die Liste kommt.
 */
function TrackWerte({
  stage,
  track,
  entry,
}: {
  stage: Stage
  track: DiaryTrack
  entry: DiaryEntry
}) {
  const hatUhr = track.startedAt !== null && track.endedAt !== null
  const zeit = etappenZeit(stage, entry)

  return (
    <>
      <dl className="strecke-werte">
        <div>
          <dt>Gefahren laut Aufzeichnung</dt>
          <dd>{num(track.distanceKm)} km</dd>
        </div>
        {hatUhr && (
          <>
            {/*
              Die Uhrzeiten der Fahrt selbst. Sie standen bisher nur in der
              Teileliste, und die gibt es erst ab der zweiten Datei - bei einer
              einzigen Aufzeichnung, dem Normalfall, war nirgends zu sehen, wann
              der Tag angefangen hat. „Aufgezeichnet“ und nicht „Aufbruch“:
              wenn das Gerät zu spät angegangen ist, sagt diese Zeile die
              Wahrheit über die Datei und nicht über die Reisenden.
            */}
            <div>
              <dt>Aufgezeichnet</dt>
              <dd>
                {clock(track.startedAt!)}–{clock(track.endedAt!)}
              </dd>
            </div>
            <div>
              <dt>Unterwegs</dt>
              <dd>{duration(track.totalSeconds)}</dd>
            </div>
            <div>
              <dt>Davon Fahrzeit</dt>
              <dd>{duration(track.movingSeconds)}</dd>
            </div>
            <div>
              <dt>Davon Standzeit</dt>
              <dd>{duration(track.stoppedSeconds)}</dd>
            </div>
            {/*
              Die Pausen stehen nur da, wo sie etwas anderes sagen als die
              Standzeit - nämlich dann, wenn ein Halt am Anfang oder am Ende der
              Aufzeichnung abgezogen wurde. Sonst wäre es dieselbe Zahl zweimal.
            */}
            {zeit && zeit.pausenH !== zeit.standH && (
              <div>
                <dt>Davon Pausen unterwegs</dt>
                <dd>{duration(zeit.pausenH * 3600)}</dd>
              </div>
            )}
            {/*
              Und die dritte Zeit, ebenfalls nur, wo sie etwas sagt: die Lücke
              zwischen zwei Dateien, in der das Fahrzeug den Ort gewechselt hat.
              Sie ist weder gefahren noch gestanden - gemessen wurde in ihr gar
              nichts -, und bei einer einzelnen Aufzeichnung gibt es sie nicht.
              Eine Zeile, die fast immer „0 min“ sagt, wäre Lärm.
            */}
            {(track.unrecordedSeconds ?? 0) > 0 && (
              <div>
                <dt>Davon nicht aufgezeichnet</dt>
                <dd>{duration(track.unrecordedSeconds!)}</dd>
              </div>
            )}
            {zeit && (
              <div>
                <dt>Schnitt in der Fahrzeit</dt>
                <dd>{num(zeit.schnittKmh)} km/h</dd>
              </div>
            )}
            {zeit && zeit.geplantH > 0 && (
              <div>
                <dt>Geplant waren</dt>
                <dd>{duration(zeit.geplantH * 3600)}</dd>
              </div>
            )}
            {/*
              Die Abweichung nur bei einer Aufzeichnung, die den Tag auch
              abdeckt: eine halbe Datei ist immer schneller als der Plan, und
              das sähe hier aus wie eine gute Nachricht.
            */}
            {zeit?.vollstaendig && zeit.geplantH > 0 && (
              <div>
                <dt>Gegenüber der Planung</dt>
                <dd>{dauerAbweichung((zeit.fahrtH - zeit.geplantH) * 3600)}</dd>
              </div>
            )}
          </>
        )}
        {track.elevation && (
          <>
            <div>
              <dt>Höhe tiefster Punkt</dt>
              <dd>{num(track.elevation.min)} m</dd>
            </div>
            <div>
              <dt>Höhe höchster Punkt</dt>
              <dd>{num(track.elevation.max)} m</dd>
            </div>
            <div>
              <dt>Anstieg gesamt</dt>
              <dd>{num(track.elevation.gain)} m</dd>
            </div>
          </>
        )}
        {/*
          Was gerade eingetragen ist, steht daneben - wie bei der
          Streckenberechnung. Ohne diesen Vergleich wäre der Knopf darunter
          eine Aufforderung, etwas zu ersetzen, das man nicht sieht.
        */}
        <div>
          <dt>Im Tagebuch jetzt</dt>
          <dd>
            {entry.realKm ? `${entry.realKm} km` : 'keine km'}
            {entry.date ? ` · ${entry.date}` : ' · kein Datum'}
          </dd>
        </div>
      </dl>

      {/*
        Warum keine Abweichung dasteht, statt dass sie einfach fehlt. Eine
        Aufzeichnung, die zu spät eingeschaltet wurde, sieht wie eine schnelle
        Fahrt aus - und das ist die eine Fehleinschätzung, die unterwegs Schaden
        anrichtet.
      */}
      {zeit && !zeit.vollstaendig && (
        <p className="hinweis">
          {zeit.abdeckung === null
            ? 'Diese Etappe hat weder eine berechnete Strecke noch einen Kilometerwert. Ob die ' +
              'Aufzeichnung den ganzen Tag abdeckt, lässt sich damit nicht sagen — die Fahrzeit ' +
              'steht deshalb ohne Vergleich da.'
            : `Die Aufzeichnung deckt ${prozent(zeit.abdeckung)} der geplanten Strecke ab. Unter ` +
              `${prozent(MIN_ABDECKUNG)} fehlen Kilometer und mit ihnen die Minuten, in denen sie ` +
              'gefahren wurden: die Fahrzeit steht deshalb ohne Vergleich da und geht in keine ' +
              'Hochrechnung ein.'}
        </p>
      )}

      {track.elevation && <Hoehenprofil elevation={track.elevation} />}

      {track.stops.length > 0 && (
        <ol className="track-stopps">
          {/*
            Der Schlüssel ist die Stelle in der Liste und nicht stop.from: eine
            Datei mit stehengebliebener Uhr kann zwei Halte mit derselben
            Anfangszeit haben, und doppelte Schlüssel liessen den zweiten
            verschwinden. Die Maschinerie aus features/stages/rowKeys.ts braucht
            es hier nicht - die löst den Fall, dass ein Eintrag aus der Mitte
            einer Liste von Eingabefeldern verschwindet und der Fokus mitwandert.
            Diese Liste hat keine Felder und wird nur als Ganzes ersetzt, nämlich
            wenn eine neue Datei gelesen wird.
          */}
          {track.stops.map((stop, i) => (
            <li key={i}>
              <b>{duration((stop.to - stop.from) / 1000)}</b>
              <span>
                {clock(stop.from)}–{clock(stop.to)}
              </span>
              <span className="track-ort">
                {stop.at[0].toFixed(4)}, {stop.at[1].toFixed(4)}
              </span>
            </li>
          ))}
        </ol>
      )}

      <p className="abschluss">
        {track.parts && track.parts.length > 1
          ? `${String(track.parts.length)} Dateien, zuletzt ${track.fileName || 'GPX-Datei'} am ${isoDate(track.importedAt)}: `
          : `${track.fileName || 'GPX-Datei'}, gelesen am ${isoDate(track.importedAt)}: `}
        {num(track.pointCount)} Punkte
        {track.segments > 1 && ` aus ${String(track.segments)} Segmenten`}
        {track.dropped > 0 && `, ${String(track.dropped)} unbrauchbare übersprungen`}. Gerechnet
        wurde über alle Punkte, gespeichert ist der ausgedünnte Weg mit {num(track.geometry.length)}{' '}
        Stützpunkten.
        {/*
          Die Schwellen stehen hier nicht als Text, sondern kommen aus den
          Konstanten: was in der Anzeige behauptet wird, soll dasselbe sein,
          wonach gerechnet wurde.
        */}
        {track.stops.length > 0 &&
          ` Als Halt zählt, wer mindestens ${duration(STOP_MIN_SECONDS)} im Umkreis von ${String(STOP_RADIUS_M)} Metern bleibt — eine Ampel ist damit keiner.`}
      </p>
    </>
  )
}

/**
 * Das Hoehenprofil als Linienzug.
 *
 * Ein handgezeichnetes SVG und kein Diagramm aus einer Bibliothek: ein Verlauf
 * aus sechzig Punkten braucht weder Achsen noch Legende. So druckt es ausserdem
 * mit, und zwar ohne Zutun.
 */
function Hoehenprofil({ elevation }: { elevation: TrackElevation }) {
  const { profile, min, max } = elevation
  if (profile.length < 2) return null

  const letzterKm = profile[profile.length - 1]![0]
  const breite = letzterKm > 0 ? letzterKm : 1
  // Eine Ebene hat keine Spannweite; ohne diesen Boden teilte die Rechnung
  // durch null und der Verlauf verschwände in einer waagerechten Linie am Rand.
  const hoehe = Math.max(1, max - min)

  const punkte = profile
    .map(
      ([km, ele]) =>
        `${((km / breite) * 100).toFixed(2)},${(30 - ((ele - min) / hoehe) * 30).toFixed(2)}`,
    )
    .join(' ')

  return (
    <svg
      className="track-profil"
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      role="img"
      aria-label={`Höhenprofil von ${String(min)} bis ${String(max)} Meter über ${String(Math.round(letzterKm))} Kilometer`}
    >
      <polyline
        points={punkte}
        fill="none"
        stroke="var(--marsala)"
        strokeWidth={1.4}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}
